import React, { useEffect, useState, useCallback } from 'react';
import { Card, Col, Row, Button, Badge } from 'react-bootstrap';
import PageHeader from '../../layouts/layoutcomponents/PageHeader';
import { API_OPERATIONS_VIEW } from '../../shared/consts/api';
import { useFetch } from '@np/np_webcomponentlib';
import { formatAmount } from '../../shared/utils/formatAmount';

interface BalanceEntry {
  id: string;
  name: string;
  typeBusiness: string;
  balance: number;
  payInVolume: number;
  payOutVolume: number;
  payInCount: number;
  payOutCount: number;
}

interface BalanceSummary {
  providers: BalanceEntry[];
  merchants: BalanceEntry[];
  providerTotal: number;
  merchantTotal: number;
  generatedAt: string;
}

const BalanceCell = ({ value }: { value: number }) => {
  const formatted = formatAmount(value, 2);
  if (value < -0.01)
    return <span style={{ color: '#E53E3E', fontWeight: 600 }}>{formatted}</span>;
  if (value > 0.01)
    return <span style={{ color: '#276749', fontWeight: 600 }}>{formatted}</span>;
  return <span style={{ color: '#718096' }}>0.00</span>;
};

const BalanceTable = ({
  title,
  rows,
  total,
  totalLabel,
}: {
  title: string;
  rows: BalanceEntry[];
  total: number;
  totalLabel: string;
}) => (
  <Card className="custom-card">
    <Card.Header>
      <Card.Title>{title}</Card.Title>
      <div className="card-options">
        <Badge bg={total < -0.01 ? 'danger' : total > 0.01 ? 'success' : 'secondary'}>
          {totalLabel}: {formatAmount(total, 2)} USDT
        </Badge>
      </div>
    </Card.Header>
    <Card.Body className="p-0">
      <div className="table-responsive">
        <table className="table table-hover table-bordered mb-0" style={{ fontSize: '0.875rem' }}>
          <thead style={{ background: '#2D3748', color: '#fff' }}>
            <tr>
              <th style={{ width: 36, textAlign: 'center' }}>#</th>
              <th>Название</th>
              <th style={{ textAlign: 'right', width: 150 }}>Баланс (USDT)</th>
              <th style={{ textAlign: 'right', width: 140 }}>PayIn объём</th>
              <th style={{ textAlign: 'right', width: 140 }}>PayOut объём</th>
              <th style={{ textAlign: 'center', width: 80 }}>PayIn кол.</th>
              <th style={{ textAlign: 'center', width: 80 }}>PayOut кол.</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => (
              <tr key={row.id} style={{ background: row.balance < -0.01 ? '#FFF5F5' : row.balance > 0.01 ? '#F0FFF4' : undefined }}>
                <td style={{ textAlign: 'center', color: '#718096' }}>{i + 1}</td>
                <td style={{ fontWeight: 500 }}>{row.name}</td>
                <td style={{ textAlign: 'right' }}>
                  <BalanceCell value={row.balance} />
                </td>
                <td style={{ textAlign: 'right', color: '#4A5568' }}>{formatAmount(row.payInVolume, 2)}</td>
                <td style={{ textAlign: 'right', color: '#4A5568' }}>{formatAmount(row.payOutVolume, 2)}</td>
                <td style={{ textAlign: 'center', color: '#4A5568' }}>{row.payInCount}</td>
                <td style={{ textAlign: 'center', color: '#4A5568' }}>{row.payOutCount}</td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr>
                <td colSpan={7} style={{ textAlign: 'center', color: '#718096', padding: '2rem' }}>
                  Нет данных
                </td>
              </tr>
            )}
          </tbody>
          {rows.length > 0 && (
            <tfoot>
              <tr style={{ background: '#2D3748', fontWeight: 600, color: '#F7FAFC' }}>
                <td colSpan={2} style={{ textAlign: 'right', color: '#F7FAFC' }}>ИТОГО</td>
                <td style={{ textAlign: 'right', color: total < -0.01 ? '#FC8181' : total > 0.01 ? '#68D391' : '#CBD5E0', fontWeight: 700 }}>
                  {formatAmount(total, 2)}
                </td>
                <td style={{ textAlign: 'right', color: '#CBD5E0' }}>{formatAmount(rows.reduce((s, r) => s + r.payInVolume, 0), 2)}</td>
                <td style={{ textAlign: 'right', color: '#CBD5E0' }}>{formatAmount(rows.reduce((s, r) => s + r.payOutVolume, 0), 2)}</td>
                <td style={{ textAlign: 'center', color: '#CBD5E0' }}>{rows.reduce((s, r) => s + r.payInCount, 0)}</td>
                <td style={{ textAlign: 'center', color: '#CBD5E0' }}>{rows.reduce((s, r) => s + r.payOutCount, 0)}</td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </Card.Body>
  </Card>
);

const BalancesPage = () => {
  const [data, setData] = useState<BalanceSummary | null>(null);
  const [loading, setLoading] = useState(false);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  const [, doFetch] = useFetch(`${API_OPERATIONS_VIEW}platform-balance/summary`);

  const load = useCallback(() => {
    setLoading(true);
    doFetch({ method: 'GET' })
      .then((resp: any) => {
        setData(resp.data);
        setLastUpdated(new Date());
      })
      .finally(() => setLoading(false));
  }, [doFetch]);

  useEffect(() => {
    load();
  }, []);

  const formatTime = (d: Date) =>
    d.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit', second: '2-digit' });

  return (
    <div>
      <PageHeader titles="Балансы платформы" active="Балансы" items={['Главная']} />

      <Row className="mb-3 align-items-center">
        <Col>
          <span style={{ color: '#718096', fontSize: '0.875rem' }}>
            {lastUpdated ? `Обновлено: ${formatTime(lastUpdated)}` : 'Загрузка...'}
          </span>
        </Col>
        <Col xs="auto">
          <Button
            variant="primary"
            size="sm"
            onClick={load}
            disabled={loading}
            style={{ minWidth: 120 }}
          >
            {loading ? (
              <><span className="spinner-border spinner-border-sm me-1" role="status" /> Загрузка...</>
            ) : (
              <><i className="fe fe-refresh-cw me-1" />Обновить</>
            )}
          </Button>
        </Col>
      </Row>

      {loading && !data && (
        <div className="dimmer active" style={{ minHeight: 200 }}>
          <div className="spinner1">
            <div className="double-bounce1" />
            <div className="double-bounce2" />
          </div>
        </div>
      )}

      {data && (
        <>
          <BalanceTable
            title={`Провайдеры (${data.providers.length})`}
            rows={data.providers}
            total={data.providerTotal}
            totalLabel="Итого провайдеры"
          />
          <div className="mt-4">
            <BalanceTable
              title={`Мерчанты (${data.merchants.length})`}
              rows={data.merchants}
              total={data.merchantTotal}
              totalLabel="Итого мерчанты"
            />
          </div>
        </>
      )}
    </div>
  );
};

export default BalancesPage;
